import PrimaryInnerGlowButton, { PrimaryDefaultButton } from '@/components/buttons/primary';
import { SecondaryDefaultButton } from '@/components/buttons/secondary';
import NextImage from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import VizyBlogArticle from '@/components/vizy/VizyBlogArticle';
import { CUSTOMER_STORIES_HOME, REQUEST_DEMO } from '@/constants/routes';
import { GlobalContext } from '@/context';
import { CustomerStoryPathsQuery, CustomerStoryQuery, CustomerStoryQueryVariables } from '@/graphql';
import CustomerStoryQueryDocument from '@/graphql/queries/CustomerStory.gql';
import CustomerStoryPathsQueryDocument from '@/graphql/queries/CustomerStoryPaths.gql';
import ResourceLayout from '@/layouts/ResourceLayout';
import CraftClient from '@/lib/graphql';
import gtm from '@/lib/gtm';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { maybeNotSet } from '@/lib/typescript-helpers';
import ResourceCardsModule, { resourceCardsModuleHasCards } from '@/modules/ResourceCards';
import { COLORS } from '@/styles/color';
import { marginY, mediaBreakpointDown, mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

// #region - Components

const HeroHeadline = styled.h1`
  ${customTextSize(40, 42)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-top: ${remCalc(8)};

  ${mediaBreakpointUp('tablet')} {
    margin-top: ${remCalc(16)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(72, 70)}
  }
`;

const HeaderImage = styled(NextImage)`
  border-radius: ${remCalc(10)};
  margin-top: ${remCalc(36)};

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    margin-top: 0;
  }
`;

const MainContent = styled.section`
  display: grid;
  grid-template-columns: 100%;
  grid-row-gap: ${remCalc(36)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: auto ${remCalc(360)};
    grid-column-gap: ${remCalc(64)};
  }
`;

const HeroLogo = styled.div`
  img {
    width: auto;
    max-height: 40px;
  }
`;

const CompanyInfoBlock = styled.div`
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(10)};

  ${mediaBreakpointUp('desktop')} {
    margin: 0 0 0 auto;
    width: ${remCalc(270)};
    position: sticky;
    top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
  }
`;

const CompanyLogo = styled.div`
  padding: ${remCalc(16, 32)};
  border-bottom: 2px solid ${COLORS.WHITE};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const CompanyContent = styled.div`
  padding: ${remCalc(16, 32, 32)};
`;

const CompanyDetails = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(16)};
  margin-bottom: ${remCalc(32)};
`;

const CompanyDetailItem = styled.li`
  h6 {
    font-weight: ${FONT_WEIGHTS.BOLD};
    ${customTextSize(16, 26)}
  }

  p {
    ${customTextSize(14, 24)}
  }

  ul {
    display: flex;
    flex-direction: column;
    row-gap: ${remCalc(16)};
    margin-top: ${remCalc(16)};

    li {
      line-height: 0;

      a {
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        ${customTextSize(14, 24)}
        color: ${COLORS.BLACK};

        transition: color 0.15s ease-in-out;

        &:hover,
        &:active {
          color: ${COLORS.BLUE.LOWLIGHT};
        }
      }
    }
  }
`;

const IntegrationLogo = styled(NextImage)`
  border-radius: 100%;
  overflow: hidden;
  margin-right: ${remCalc(12)};
  box-shadow: rgb(0 27 56 / 16%) 0px 2px 8px;
  width: 24px;
`;

const CTABlock = styled.div`
  border-radius: ${remCalc(10)};
  background-color: ${COLORS.BLACK};
  padding: ${remCalc(32)};
  ${marginY(40)};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  row-gap: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    justify-content: space-between;
    padding: ${remCalc(42, 104)};
    min-height: ${remCalc(168)};
  }

  p {
    ${customTextSize(40, 42)};
    color: ${COLORS.WHITE};
    text-align: center;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};

    ${mediaBreakpointUp('desktop')} {
      text-align: left;
    }
  }

  div {
    display: flex;
    flex-direction: column;
    gap: ${remCalc(16)};

    ${mediaBreakpointUp('desktop')} {
      flex-direction: row;
    }
  }

  ${PrimaryDefaultButton} {
    --button-color: ${COLORS.WHITE};
    --button-text-color: ${COLORS.BLACK};
    --button-color-hover: ${COLORS.BLUE.LOWLIGHT};
    --button-text-color-hover: ${COLORS.WHITE};

    ${mediaBreakpointDown('mobile')} {
      width: 100%;
    }
  }

  ${SecondaryDefaultButton} {
    --button-text-color: ${COLORS.WHITE};
    --button-color-hover: ${COLORS.WHITE};
    --button-text-color-hover: ${COLORS.BLACK};
  }
`;

// #endregion - Components

interface PageProps extends CustomerStoryQuery {}

const CustomerStory: PageTemplate<PageProps> = ({ entry }) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);

  const customerLogo = useMemo(() => {
    const logo = entry?.customer?.[0]?.customerLogo && entry?.customer?.[0]?.customerLogo[0];

    if (!logo) return null;

    return <NextImage src={logo.url!} width={logo.width!} height={logo.height!} alt={logo.altText ?? ''} />;
  }, [entry]);

  const companyDetails = useMemo(() => {
    const entries: { heading: string; value: string }[] = [];

    if (entry?.customer?.[0]?.industryCategories?.length) {
      entries.push({
        heading: 'Industry',
        value: entry.customer?.[0]?.industryCategories.map((industry) => industry?.title).join(', '),
      });
    }

    if (entry?.customer?.[0]?.teamCategories?.length) {
      entries.push({
        heading: 'Team',
        value: entry.customer?.[0]?.teamCategories.map((team) => team?.title).join(', '),
      });
    }

    if (entry?.customer?.[0]?.customerData?.length) {
      /* @ts-ignore */
      entries.push(...entry.customer?.[0]?.customerData);
    }

    return entries;
  }, [entry]);

  const coverImage = entry?.coverImage && entry?.coverImage?.[0];

  return (
    <>
      <ResourceLayout
        backButton={{
          href: CUSTOMER_STORIES_HOME,
          onClick: () => {
            gtm.track('click', {
              element_type: 'link',
              element_label: 'Back to Customer Story Home',
              location: 'Customer Story Header',
            });
          },
        }}
        headerTopBar={<HeroLogo>{customerLogo}</HeroLogo>}
        headerLeftColumn={<HeroHeadline>{entry?.heroTitle}</HeroHeadline>}
        headerRightColumn={
          coverImage && (
            <HeaderImage
              src={coverImage.url!}
              width={coverImage.width!}
              height={coverImage.height!}
              alt={coverImage.altText ?? ''}
            />
          )
        }
      >
        <MainContent>
          <article>
            <VizyBlogArticle nodes={entry?.blogArticleContent?.nodes} rawNodes={entry?.blogArticleContent?.rawNodes} />
          </article>

          <aside>
            <CompanyInfoBlock>
              <CompanyLogo>{customerLogo}</CompanyLogo>

              <CompanyContent>
                <CompanyDetails>
                  {companyDetails.map(({ heading, value }) => (
                    <CompanyDetailItem key={heading}>
                      <h6>{heading}</h6>
                      <p>{value}</p>
                    </CompanyDetailItem>
                  ))}

                  {!!entry?.customer?.[0]?.integrations?.length && (
                    <CompanyDetailItem>
                      <h6>Tools integrated with Front</h6>
                      <ul>
                        {entry?.customer?.[0]?.integrations?.map((integration) => {
                          const logo = integration?.integrationLogo && integration?.integrationLogo?.[0];

                          return (
                            <li key={integration?.id}>
                              <Link href={integration?.url ?? '#'}>
                                <a
                                  onClick={() => {
                                    gtm.track('click', {
                                      element_label: 'Integration Logo',
                                      integration_title: maybeNotSet(integration?.title),
                                      location: 'Customer Info Block',
                                    });
                                  }}
                                  title={`Read more about the ${integration?.title} integration`}
                                >
                                  {!!logo && (
                                    <IntegrationLogo
                                      src={logo.url!}
                                      width={logo.width!}
                                      height={logo.height!}
                                      sizes="24px"
                                      alt={`${integration?.title} logo`}
                                    />
                                  )}
                                  <span>{integration?.title}</span>
                                </a>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </CompanyDetailItem>
                  )}
                </CompanyDetails>

                <PrimaryInnerGlowButton
                  onClick={() => {
                    toggleGetStartedModal(true);

                    gtm.track('click', {
                      element_label: 'Get Started',
                      location: 'Customer Info Block',
                      element_type: 'button',
                    });
                  }}
                  fullWidth
                >
                  Get started
                </PrimaryInnerGlowButton>
              </CompanyContent>
            </CompanyInfoBlock>
          </aside>
        </MainContent>
      </ResourceLayout>

      {resourceCardsModuleHasCards(entry?.moduleResourceCards) ? (
        <Container>
          <ResourceCardsModule props={entry?.moduleResourceCards} />
        </Container>
      ) : null}

      <Container>
        <CTABlock>
          <p>
            Want to see Front in action?
            <br />
            Request a demo!
          </p>
          <div>
            <PrimaryDefaultButton
              onClick={() => {
                toggleGetStartedModal(true);

                gtm.track('click', {
                  element_type: 'button',
                  element_label: 'Get Started',
                  location: 'CTA Block',
                });
              }}
            >
              Get started
            </PrimaryDefaultButton>

            <Link href={REQUEST_DEMO}>
              <SecondaryDefaultButton
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    element_type: 'link',
                    element_label: 'Contact sales',
                    location: 'CTA Block',
                  });
                }}
              >
                Contact sales
              </SecondaryDefaultButton>
            </Link>
          </div>
        </CTABlock>
      </Container>
    </>
  );
};

CustomerStory.template = MasterTemplate;

export default CustomerStory;

export const getStaticPaths = getStaticPagePaths('customer-stories', async (context) => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const client = new CraftClient();

  const data = await client.request<CustomerStoryPathsQuery>(CustomerStoryPathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});

export const getStaticProps = getStaticPageProps<{}, { slug: string }>(
  (params) => `customer-stories/${params?.slug}`,
  async ({ client, context: { params } }) => {
    const data = await client.request<CustomerStoryQuery, CustomerStoryQueryVariables>(CustomerStoryQueryDocument, {
      slug: params?.slug,
    });

    if (!data.entry) {
      return { notFound: true };
    }

    return {
      props: {
        ...data,
      },
    };
  },
);
